import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth";
import { useToasts } from "../../contexts/toasts";
import { Programmazione } from "../../types/Programmazione";
import { Programmazioni } from "../../api/Programmazioni";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { Alert, Breadcrumb, Button, Flex, Grid, Space, theme } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSave, faCopy, faPaste } from "@fortawesome/free-solid-svg-icons";
import { useClipboard } from "../../contexts/clipboard";
import { TailSpin } from "react-loader-spinner";
import Title from "antd/es/typography/Title";
import ProgrammazioneForm from "../../Components/Form/ProgrammazioneForm";
import StickySaveBar from "../../Components/StickySaveBar";
import "./EditProgrammazione.css";
import { Option } from "../../types/generics";
import { useAssignableUsers } from "../../hooks/useAssignableUsers";
import { useEntityOptions, type ProgEditorOptions } from "../../hooks/useEntityOptions";
import { Entities } from "../../api/Entities";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { stripLegacyExerciseFields } from "../../utils/splitExerciseStripNormalizer";
import { appendMediaFiles } from "../../utils/programmazioneMediaAppender";

export default function EditProgrammazione() {
  const { id, action } = useParams();
  const navigate = useNavigate();

  const { loggedUser } = useAuth();

  const { showToast } = useToasts();
  const { isDirty, markSaved, checkDirty, safeNavigate } = useUnsavedChanges();
  const { users: assignableUsers, loading: loadingUsers } = useAssignableUsers();
  // PROG-WEEK-PARAMS/FE-1 — lift catalogs to the page so all
  // ExerciseProgressionEditor instances under the form share a single
  // fetch + cache (Q5). Avoids the per-ExerciseCard hook fanout that
  // would otherwise issue ~30 redundant catalog requests on a typical
  // programmazione load.
  const {
    options: progressionEntityOptions,
    loadRepSchemesByType,
    loadEffortsByType,
  } = useEntityOptions();
  const progEditorOptions = useMemo<ProgEditorOptions>(
    () => ({
      repSchemeTypeOptions: progressionEntityOptions.repSchemeTypes,
      effortTypeOptions: progressionEntityOptions.effortTypes,
      tutOptions: progressionEntityOptions.tuts,
      tecnicaOptions: progressionEntityOptions.tecniche,
      tecnicheRaw: progressionEntityOptions.tecnicheRaw,
      recOptions: progressionEntityOptions.recs,
      loadRepSchemesByType,
      loadEffortsByType,
    }),
    [progressionEntityOptions, loadRepSchemesByType, loadEffortsByType],
  );
  const { copySplits, copiedSplits } = useClipboard();
  const [programmazione, setProgrammazione] = useState<Programmazione>();
  const [formKey, setFormKey] = useState(0);
  const [exercisesOptions, setExercisesOptions] = useState<Option[]>([]);
  const [muscleGroupOptions, setMuscleGroupOptions] = useState<Option[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // PROG-DETAIL/IMPL M1 — track whether the user dismissed the dirty
  // banner so we can hide it on mobile until isDirty toggles back to true.
  const [dirtyAlertDismissed, setDirtyAlertDismissed] = useState(false);

  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  const programmazioneFormDisabled = useMemo(() => {
    return (
      !programmazione ||
      loading ||
      !programmazione.name ||
      !programmazione.splits?.length
    );
  }, [programmazione, loading]);

  const initProgrammazione = useCallback(() => {
    Programmazioni.getProgrammazione(id!).then(
      ({ data }) => {
        // SPLIT-LEAN/B — strip legacy per-exercise programmazione fields
        // (sets/weeks/precompiledRef) on read. Lazy migration: existing rows
        // carry these fields; new saves emit the lean shape.
        const normalizedProg = stripLegacyExerciseFields(data.programmazione);
        setProgrammazione(normalizedProg);
        markSaved(normalizedProg);
      },
      () => {
        showToast(
          "programmazione-error",
          "Errore nel reperimento della Programmazione",
          "",
          "danger"
        );
        setError(true);
      }
    );
  }, [id, showToast, markSaved]);

  const initExercises = useCallback(() => {
    Entities.getExercises().then(
      ({ data }) =>
        setExercisesOptions(
          data.list.map((exercise) => ({
            label: exercise.name,
            value: exercise,
          }))
        ),
      () => {
        showToast(
          "exercises-error",
          "Errore nel reperimento degli Esercizi",
          "",
          "danger"
        );
        setError(true);
      }
    );
  }, [showToast]);

  const initMuscleGroups = useCallback(() => {
    Entities.getMuscleGroups().then(
      ({ data }) =>
        setMuscleGroupOptions(
          data.list.map((mg) => ({ label: mg.muscleGroup, value: mg.id })),
        ),
      () => {
        /* non-blocking: the selector just stays empty on failure */
      },
    );
  }, []);

  const reset = useCallback(() => {
    setError(false);
    setProgrammazione(undefined);
    setExercisesOptions([]);
    initProgrammazione();
  }, [initProgrammazione]);

  useEffect(() => {
    initExercises();
    initMuscleGroups();
    if (id && id !== "create") {
      reset();
    } else {
      if (loggedUser) {
        const newProgrammazione = new Programmazione(loggedUser.id!);
        setProgrammazione(newProgrammazione);
        markSaved(newProgrammazione);
      } else navigate("/programmazioni");
    }
  }, [reset, id, loggedUser, navigate, initExercises, initMuscleGroups]);

  // PROG-DETAIL/IMPL M1 — re-arm the dirty banner whenever the form
  // transitions back to clean, so a future dirty edit shows it again.
  useEffect(() => {
    if (!isDirty) setDirtyAlertDismissed(false);
  }, [isDirty]);

  const handleSave = useCallback(async () => {
    if (!programmazione || programmazioneFormDisabled) return;

    setLoading(true);
    setSaveError(null);

    try {
      const formData = new FormData();

      // PROG-SET-MEDIA/FE-SAVE-1 — deep-clone exercises AND their nested
      // `progression.weeks[*].sets[*]` so that the appender's in-place
      // `delete image|video` does not mutate the React state. Splits and
      // sets at structural level still spread-clone, since the appender
      // only deletes the media-File keys, not deeper structure.
      const programmazioneToSave: any = {
        ...programmazione,
        splits: programmazione.splits?.map((split) => ({
          ...split,
          exercises: split.exercises.map((exercise: any) => ({
            ...exercise,
            progression: exercise.progression
              ? {
                  ...exercise.progression,
                  weeks: (exercise.progression.weeks ?? []).map((week: any) => ({
                    ...week,
                    sets: (week.sets ?? []).map((set: any) => ({ ...set })),
                  })),
                }
              : exercise.progression,
          })),
        })),
      };

      appendMediaFiles(programmazioneToSave, formData);

      // Ensure nSplits is set
      programmazioneToSave.nSplits = programmazioneToSave.splits?.length || 0;

      // Normalize scadenza to an ISO string. It may be a dayjs object (from the
      // DatePicker), a Date, or already a string — the BE expects a parseable
      // string. Both dayjs and Date expose toISOString(); guard for it so the
      // date always round-trips instead of being dropped.
      if (
        programmazioneToSave.scadenza &&
        typeof programmazioneToSave.scadenza === "object" &&
        typeof programmazioneToSave.scadenza.toISOString === "function"
      ) {
        programmazioneToSave.scadenza = programmazioneToSave.scadenza.toISOString();
      }

      // Add programmazione data as JSON string
      formData.append("programmazione", JSON.stringify(programmazioneToSave));
      
      // Call appropriate API method
      if (id && id !== "create") {
        await Programmazioni.updateProgrammazione(id, formData);
        markSaved(programmazione);
        showToast(
          "programmazione-updated",
          "Programmazione aggiornata con successo",
          "",
          "success"
        );
      } else {
        const { data } = await Programmazioni.createProgrammazione(formData);
        markSaved(programmazione);
        showToast(
          "programmazione-created",
          "Programmazione creata con successo",
          "",
          "success"
        );
        /* T1.C (#76355a42) — stay on page after CREATE save. Flip URL to
           /:newId/edit (replace) so subsequent saves go through the update
           branch (no duplicate-create). UPDATE branch already stays put. */
        navigate(`/programmazioni/${data.programmazione.id}/edit`, {
          replace: true,
        });
      }
    } catch (error: any) {
      console.error("Error saving programmazione:", error);
      const detail =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        null;
      setSaveError(
        detail
          ? `Errore nel salvataggio della programmazione: ${detail}`
          : "Errore nel salvataggio della programmazione."
      );
      showToast(
        "programmazione-save-error",
        "Errore nel salvataggio della programmazione",
        "",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  }, [programmazione, programmazioneFormDisabled, id, showToast, navigate]);

  const handleFormChange = useCallback((changedValues: any, allValues: Programmazione) => {
    setProgrammazione(allValues);
    checkDirty(allValues);
  }, [checkDirty]);

  const handlePasteSplits = useCallback(() => {
    if (!copiedSplits || !programmazione) return;
    const pasted: Programmazione = {
      ...programmazione,
      splits: JSON.parse(JSON.stringify(copiedSplits)),
      nSplits: copiedSplits.length,
    };
    setProgrammazione(pasted);
    checkDirty(pasted);
    setFormKey((k) => k + 1);
  }, [copiedSplits, programmazione, checkDirty]);

  const breadcrumbItems = useMemo(() => {
    if (!id || (id && action === "create"))
      return [
        {
          title: (
            <span
              style={{ textDecoration: "none", cursor: "pointer" }}
              onClick={() => safeNavigate("/programmazioni")}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Programmazioni
            </span>
          ),
        },
        {
          title: "Nuova Programmazione",
        },
      ];
    return [
      {
        title: (
          <span
            style={{ textDecoration: "none", cursor: "pointer" }}
            onClick={() => safeNavigate("/programmazioni")}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Programmazioni
          </span>
        ),
      },
      {
        title: (
          <span
            style={{ textDecoration: "none", cursor: "pointer" }}
            onClick={() => safeNavigate(`/programmazioni/${id}`)}
          >
            {programmazione?.name || "Programmazione"}
          </span>
        ),
      },
      {
        title: "Modifica",
      },
    ];
  }, [id, action, programmazione, safeNavigate]);

  return (
    <Container fluid="lg" className="my-3 sticky-save-bar-spacer">
      <Helmet>
        <title>{id ? "Modifica" : "Nuova"} Programmazione</title>
      </Helmet>

      <Breadcrumb items={breadcrumbItems} className="mb-3" />
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Si è verificato un errore nel reperimento dei dati della Programmazione Riprova più tardi."
        />
      ) : (
        <>
          {programmazione ? (
            (() => {
              const isCreateMode = !id || (id && action === "create");
              const titleText = isCreateMode
                ? programmazione.name || "Nuova Programmazione"
                : programmazione.name || "Modifica Programmazione";
              const saveLabel = isCreateMode ? "Salva" : "Salva Modifiche";

              const showCopy = !isCreateMode && (programmazione.splits?.length ?? 0) > 0;
              const showPaste = !!copiedSplits;

              const handleCopy = () => {
                copySplits(programmazione.splits);
                showToast("copy-programmazione", "Programmazione copiata", "", "success");
              };

              const saveDisabled =
                programmazioneFormDisabled || (!isCreateMode && loading);

              const desktopActions = (
                <Flex gap={8}>
                  {showCopy && (
                    <Button
                      type="primary"
                      onClick={handleCopy}
                      icon={<FontAwesomeIcon icon={faCopy} />}
                      style={{ background: token.colorPrimary }}
                    >
                      <span className="d-none d-sm-inline">Copia</span>
                    </Button>
                  )}
                  {showPaste && (
                    <Button
                      type="primary"
                      onClick={handlePasteSplits}
                      icon={<FontAwesomeIcon icon={faPaste} />}
                      style={{ background: token.colorSuccess }}
                    >
                      <span className="d-none d-sm-inline">Incolla</span>
                    </Button>
                  )}
                  <span className="prog-form-save-cta">
                    <Button
                      type="primary"
                      onClick={handleSave}
                      disabled={saveDisabled}
                      className={isDirty ? "button-dirty" : undefined}
                      icon={
                        !isCreateMode && loading ? (
                          <TailSpin height={16} width={16} color="white" radius="1" />
                        ) : (
                          <FontAwesomeIcon icon={faSave} />
                        )
                      }
                    >
                      <span className="d-none d-sm-inline">{saveLabel}</span>
                    </Button>
                  </span>
                </Flex>
              );


              return (
                <>
                  <div
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      background: token.colorBgContainer,
                      padding: "12px 16px",
                      marginInline: -16,
                      borderBottom: `1px solid ${token.colorBorderSecondary}`,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                  >
                    <Flex
                      justify="space-between"
                      align="center"
                      className="flex-column flex-md-row"
                      gap={isMobile ? 12 : 8}
                    >
                      <div className="text-center text-md-start">
                        {/* PROG-DETAIL/IMPL M1 — drop to level=2 on <sm to
                            shave ~40px off the sticky-header height when
                            isDirty + saveError alerts also render. */}
                        <Title level={isMobile ? 2 : 1} className="mb-0">
                          {titleText}
                        </Title>
                      </div>
                      {desktopActions}
                    </Flex>
                    {(isDirty || saveError) && (
                      <Space
                        direction="vertical"
                        size={8}
                        style={{ width: "100%", marginTop: 8 }}
                      >
                        {isDirty && !dirtyAlertDismissed && (
                          <Alert
                            type="warning"
                            banner
                            // PROG-DETAIL/IMPL M1 — dismissible on <sm so
                            // the user can reclaim sticky-header height
                            // after acknowledging the dirty state. Banner
                            // re-appears on the next isDirty transition.
                            closable={isMobile}
                            onClose={() => setDirtyAlertDismissed(true)}
                            message="Modifiche non salvate."
                          />
                        )}
                        {saveError && (
                          <Alert
                            type="error"
                            showIcon
                            closable
                            onClose={() => setSaveError(null)}
                            message={saveError}
                          />
                        )}
                      </Space>
                    )}
                  </div>
                  <ProgrammazioneForm
                    key={formKey}
                    programmazione={programmazione}
                    exercisesOptions={exercisesOptions}
                    muscleGroupOptions={muscleGroupOptions}
                    onValuesChange={handleFormChange}
                    assignableUsers={assignableUsers}
                    loadingUsers={loadingUsers}
                    isCreateMode={!!isCreateMode}
                    progEditorOptions={progEditorOptions}
                  />
                </>
              );
            })()
          ) : (
            <div className="d-flex justify-content-center py-5 my-5">
              <TailSpin height={100} width={100} color="#9c1419" radius="1" />
            </div>
          )}
        </>
      )}
      {programmazione && (
        <StickySaveBar
          onSave={handleSave}
          saving={loading}
          dirty={isDirty}
        />
      )}
    </Container>
  );
}
